import { FiSearch, FiCheckCircle, FiXCircle, FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import { maskDocument } from '../../credit-analysis/utils/documentUtils';
import { PartyRole, type SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCasePartyForm, {
    isCompleteDocument,
    residualErrors,
    type AddPartyFormValue,
} from '../../../../hooks/useLegalCasePartyForm';

export type { AddPartyFormValue };

interface LegalCasePartyFormProps {
    roleOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddPartyFormValue) => Promise<boolean>;
    onCancel: () => void;
}

// Só JSX + bindings: estado e regras do formulário vivem em useLegalCasePartyForm.
const LegalCasePartyForm = ({
    roleOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCasePartyFormProps) => {
    const form = useLegalCasePartyForm({ onSubmit });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <SelectTextField
                label="Papel"
                items={roleOptions}
                value={form.role}
                // O select só oferece papéis válidos (page-form ou fallback local), então o
                // narrowing string→PartyRole é seguro; é o único ponto de conversão.
                onChange={(e) => form.setRole(e.target.value as PartyRole)}
                errorMessage={getErrorMessage(errorFields, 'role')}
            />

            {!form.externalMode && (
                <div className="space-y-2">
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <TextEdit
                                label="CPF / CNPJ da parte"
                                value={form.document}
                                onChange={(e: any) => form.changeDocument(e.target.value)}
                                onKeyDown={(e: any) => {
                                    if (e.key === 'Enter') form.search();
                                }}
                            />
                        </div>
                        <button
                            onClick={form.search}
                            disabled={!isCompleteDocument(form.document) || form.resolved.state === 'searching'}
                            className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                            title="Buscar em Pessoas"
                        >
                            <FiSearch size={14} />
                            {form.resolved.state === 'searching' ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>

                    {form.resolved.state === 'found' && (
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                            <FiCheckCircle size={14} />
                            <span className="truncate">
                                {form.resolved.person.name}
                                {form.resolved.person.document ? ` — ${maskDocument(form.resolved.person.document)}` : ''}
                            </span>
                        </div>
                    )}

                    {form.resolved.state === 'notFound' && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                                <FiXCircle size={14} />
                                <span>Documento não encontrado em Pessoas.</span>
                            </div>
                            <button
                                onClick={form.enableExternalMode}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                Cadastrar como contraparte externa (sem cadastro)
                            </button>
                        </div>
                    )}

                    {form.resolved.state === 'error' && (
                        <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                            <FiAlertTriangle size={14} />
                            <span>Não foi possível consultar Pessoas. Tente novamente.</span>
                        </div>
                    )}

                    <button
                        onClick={form.enableExternalMode}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                    >
                        A parte não tem cadastro em Pessoas? Informar manualmente
                    </button>
                </div>
            )}

            {form.externalMode && (
                <div className="space-y-2">
                    <TextEdit
                        label="Nome da contraparte"
                        value={form.externalName}
                        onChange={(e: any) => form.setExternalName(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'name')}
                    />
                    <TextEdit
                        label="CPF / CNPJ (opcional)"
                        value={form.document}
                        onChange={(e: any) => form.changeDocument(e.target.value)}
                    />
                    <button
                        onClick={form.backToSearch}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                    >
                        Buscar por documento em vez disso
                    </button>
                </div>
            )}

            <TextEdit
                label="Observação"
                value={form.description}
                onChange={(e: any) => form.setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div
                    key={e.fieldName}
                    className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400"
                >
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Salvar parte'}
                </button>
            </div>
        </div>
    );
};

export default LegalCasePartyForm;
